// src/components/EditUserModal.tsx - NEW: Modal for editing user details
import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, Check, Shield, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAppContext } from '../hooks/useAppContext';
import { formatPrice } from '../types';
import './shared.css';
import { parseCustomRate, RATE_PERIODS } from '../utils/rateLimits';

interface User {
  api_key: string;
  name: string;
  permissions: string[];
  is_active: boolean;
  rate_limit?: string | null;
  search_preview_limited?: boolean;
  created_at: string;
  updated_at: string;
  custom_price_per_contact?: string | number | null;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userToEdit: User | null;
  onUserUpdated: () => void;
}

interface FormData {
  name: string;
  permissions: string[];
  is_active: boolean;
  search_preview_limited: boolean;
  rateLimitMode: 'default' | 'off' | 'custom';
  rateLimitCustom: string;
  custom_price_per_contact: string;
  pricing_change_reason: string;
}

const AVAILABLE_PERMISSIONS = [
  'search',
  'metadata', 
  'health',
  'export',
  'contacts',
  'suggestions',
  'admin'
];

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  userToEdit,
  onUserUpdated
}) => {
  const { getUser, updateUser, setCustomPricing } = useApi();
  const { showToast, state } = useAppContext();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    permissions: [],
    is_active: true,
    search_preview_limited: false,
    rateLimitMode: 'default',
    rateLimitCustom: '',
    custom_price_per_contact: '',
    pricing_change_reason: ''
  });
  const [originalCustomPrice, setOriginalCustomPrice] = useState<string | number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ratePolicy = state.schema?.ui_config?.rate_limits;
  const customPeriod = formData.rateLimitCustom.split('/')[1] || 'minute';
  const customCount = formData.rateLimitCustom.split('/')[0];
  const globalPrice = state.schema?.ui_config?.billing?.price_per_contact || '0.02';

  // Load user details when modal opens
  useEffect(() => {
    if (isOpen && userToEdit) {
      setIsLoading(true);
      setError(null);

      // Fetch full user details
      getUser(userToEdit.api_key)
        .then(userData => {
          if (userData) {
            const typedUserData = userData as User;
            const customPrice = typedUserData.custom_price_per_contact;
            const customPriceStr = customPrice ? String(customPrice) : '';
            const rateLimit = (typedUserData.rate_limit || '').trim().toLowerCase();
            setOriginalCustomPrice(customPrice || null);
            setFormData({
              name: typedUserData.name,
              permissions: typedUserData.permissions || [],
              is_active: typedUserData.is_active,
              search_preview_limited: typedUserData.search_preview_limited ?? false,
              rateLimitMode: rateLimit === 'off' ? 'off' : rateLimit ? 'custom' : 'default',
              rateLimitCustom: rateLimit && rateLimit !== 'off' ? rateLimit : '',
              custom_price_per_contact: customPriceStr,
              pricing_change_reason: ''
            });
          } else {
            throw new Error('User data not found');
          }
        })
        .catch(err => {
          console.error('Failed to load user details:', err);
          setError('Failed to load user details');
          showToast({
            type: 'error',
            title: 'Loading Error',
            message: 'Failed to load user details'
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, userToEdit, getUser, showToast]);

  // Handle form submission
  const handleSave = useCallback(async () => {
    if (!userToEdit) return;

    if (!formData.name.trim()) {
      showToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please enter a user name'
      });
      return;
    }

    if (formData.permissions.length === 0) {
      showToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Please select at least one permission'
      });
      return;
    }

    // Check if custom pricing changed
    const newCustomPrice = formData.custom_price_per_contact.trim();
    const hasCustomPrice = newCustomPrice !== '';
    const pricingChanged = (hasCustomPrice ? newCustomPrice : null) !== (originalCustomPrice ? String(originalCustomPrice) : null);

    // Validate custom pricing fields
    if (pricingChanged) {
      if (hasCustomPrice) {
        const priceNum = parseFloat(newCustomPrice);
        if (isNaN(priceNum) || priceNum < 0.001 || priceNum > 1.0) {
          showToast({
            type: 'warning',
            title: 'Validation Error',
            message: 'Custom price must be between $0.001 and $1.00'
          });
          return;
        }
      }
      if (!formData.pricing_change_reason.trim() || formData.pricing_change_reason.trim().length < 10) {
        showToast({
          type: 'warning',
          title: 'Validation Error',
          message: 'Please provide a reason for the pricing change (minimum 10 characters)'
        });
        return;
      }
    }

    if (formData.rateLimitMode === 'custom' && !parseCustomRate(formData.rateLimitCustom)) {
      showToast({
        type: 'warning',
        title: 'Validation Error',
        message: 'Enter a positive whole-number request limit and choose a time period.'
      });
      return;
    }

    setIsSaving(true);
    try {
      const rateLimit = formData.rateLimitMode === 'off'
        ? 'off'
        : formData.rateLimitMode === 'custom'
          ? formData.rateLimitCustom.trim().toLowerCase()
          : null;

      // Update user core details
      await updateUser(userToEdit.api_key, {
        name: formData.name.trim(),
        permissions: formData.permissions,
        is_active: formData.is_active,
        search_preview_limited: formData.search_preview_limited,
        rate_limit: rateLimit
      });

      // Update custom pricing if changed
      if (pricingChanged) {
        const customPriceValue = hasCustomPrice ? parseFloat(newCustomPrice) : null;
        await setCustomPricing(userToEdit.api_key, customPriceValue, formData.pricing_change_reason.trim());
      }

      showToast({
        type: 'success',
        title: 'User Updated',
        message: `User "${formData.name}" has been updated successfully${pricingChanged ? ' (including custom pricing)' : ''}`
      });

      onUserUpdated();
      onClose();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: error instanceof Error ? error.message : 'Failed to update user'
      });
    } finally {
      setIsSaving(false);
    }
  }, [userToEdit, formData, originalCustomPrice, updateUser, setCustomPricing, showToast, onUserUpdated, onClose]);

  // Toggle permission
  const togglePermission = useCallback((permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen || !userToEdit) return null;

  return (
    <div className="modal-overlay" onClick={isSaving ? undefined : onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit User</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={isSaving}
          >
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-container">
              <Loader2 size={32} className="loading-spinner" />
              <p>Loading user details...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <AlertCircle size={32} />
              <h3>Error Loading User</h3>
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="info-box">
                <Shield size={16} />
                <span>Editing user: <strong>{userToEdit.name}</strong></span>
              </div>

              <div className="form-group">
                <label htmlFor="edit-name" className="form-label">User Name</label>
                <input
                  id="edit-name"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter user name..."
                  disabled={isSaving}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Permissions</label>
                <div className="grid grid-3" style={{ gap: '8px', marginTop: '8px' }}>
                  {AVAILABLE_PERMISSIONS.map(permission => (
                    <button
                      key={permission}
                      type="button"
                      className={`btn btn-ghost ${formData.permissions.includes(permission) ? 'btn-primary' : ''}`}
                      onClick={() => togglePermission(permission)}
                      disabled={isSaving}
                      style={{ justifyContent: 'flex-start' }}
                    >
                      <div style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid currentColor', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '8px'
                      }}>
                        {formData.permissions.includes(permission) && <Check size={10} />}
                      </div>
                      <span style={{ textTransform: 'capitalize' }}>{permission}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Custom Pricing (Optional)</label>
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                  Global price: ${formatPrice(globalPrice)} per contact. Leave empty to use global pricing.
                  {originalCustomPrice && <div style={{ marginTop: '4px', color: '#2563eb' }}>Current custom price: ${formatPrice(originalCustomPrice)}</div>}
                </div>
                <input
                  id="custom-price"
                  type="number"
                  step="0.001"
                  min="0.001"
                  max="1.0"
                  value={formData.custom_price_per_contact}
                  onChange={e => setFormData(prev => ({ ...prev, custom_price_per_contact: e.target.value }))}
                  placeholder="e.g., 0.015 (leave empty for global pricing)"
                  disabled={isSaving}
                  className="form-input"
                  style={{ marginBottom: '8px' }}
                />
                <label htmlFor="pricing-reason" className="form-label" style={{ marginTop: '8px', fontSize: '12px' }}>
                  Reason for pricing change {formData.custom_price_per_contact.trim() !== (originalCustomPrice ? String(originalCustomPrice) : '') && <span style={{ color: 'red' }}>*</span>}
                </label>
                <textarea
                  id="pricing-reason"
                  value={formData.pricing_change_reason}
                  onChange={e => setFormData(prev => ({ ...prev, pricing_change_reason: e.target.value }))}
                  placeholder="Explain why custom pricing is being set/changed (minimum 10 characters)..."
                  disabled={isSaving}
                  className="form-input"
                  rows={3}
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="edit-rate-limit">Customer request limit</label>
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                  Applies only to this customer. Default search: {ratePolicy?.defaults.search || 'not available'}; metadata: {ratePolicy?.defaults.metadata || 'not available'}; general fallback: {ratePolicy?.defaults.default || 'not available'}.
                  <br />Custom replaces the normal limit separately on each endpoint. No rate limit removes this customer’s request quota, not other customers’ protections.
                  <br />There is no universal recommended maximum: query cost varies. Start with Default for ordinary use. Changes reach all API workers within about {ratePolicy?.refresh_seconds || 10} seconds; server-busy responses are separate from account quotas.
                </div>
                <select
                  id="edit-rate-limit"
                  className="form-input"
                  value={formData.rateLimitMode}
                  onChange={e => setFormData(prev => ({ ...prev, rateLimitMode: e.target.value as FormData['rateLimitMode'], rateLimitCustom: prev.rateLimitCustom || '600/minute' }))}
                  disabled={isSaving}
                  style={{ marginBottom: formData.rateLimitMode === 'custom' ? '8px' : 0 }}
                >
                  <option value="default">Default — standard endpoint limits</option>
                  <option value="off">No rate limit — this customer only</option>
                  <option value="custom">Custom — requests per time period</option>
                </select>
                {formData.rateLimitMode === 'custom' && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input id="edit-rate-limit-custom" type="number" min="1" max="2147483647" step="1"
                      aria-label="Requests per time period" value={customCount} placeholder="600"
                      onChange={e => setFormData(prev => ({ ...prev, rateLimitCustom: `${e.target.value}/${customPeriod}` }))}
                      disabled={isSaving} className="form-input" />
                    <span>per</span>
                    <select aria-label="Rate limit time period" value={customPeriod}
                      onChange={e => setFormData(prev => ({ ...prev, rateLimitCustom: `${customCount}/${e.target.value}` }))}
                      disabled={isSaving} className="form-input">
                      {RATE_PERIODS.map(period => <option key={period} value={period}>{period}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    disabled={isSaving}
                    style={{ marginRight: '8px' }}
                  />
                  User is active
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={formData.search_preview_limited}
                    onChange={e => setFormData(prev => ({ ...prev, search_preview_limited: e.target.checked }))}
                    disabled={isSaving} style={{ marginRight: '8px' }} />
                  Limit People and Company searches to 75 results
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  Applies to this user in the website and API, per search, regardless of payment status.
                  Saved Contacts, Accounts, and existing unlock jobs remain available.
                  Uncheck to restore normal search access. Off by default.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || isLoading || !!error}
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="loading-spinner" />
                Updating...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
